import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  DestinationSettingsAppearance,
} from './destination-settings-panel.component';
import {
  DestinationPlatformData,
  YouTubeDestinationPlatformData,
  YouTubeVideoCategoryCatalog,
} from '../../../domain/destinations';
import { ToastrService } from 'ngx-toastr';

const YOUTUBE_PRIVACY_OPTIONS = ['Private', 'Unlisted', 'Public'] as const;

@Component({
  selector: 'evrideo-destination-youtube-settings',
  templateUrl: './destination-youtube-settings.component.html',
  styleUrls: ['./destination-youtube-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DestinationYoutubeSettingsComponent implements OnChanges {
  @Input({ required: true }) catalog!: YouTubeVideoCategoryCatalog;
  @Input() platformData: DestinationPlatformData | null = null;
  @Input() readonly = false;
  @Input() appearance: DestinationSettingsAppearance = 'default';

  @Output() platformDataChange = new EventEmitter<YouTubeDestinationPlatformData>();

  readonly privacyOptions = YOUTUBE_PRIVACY_OPTIONS;

  selectedPrivacy: string | null = null;
  madeForKids = false;
  notifySubscribers = false;
  selectedCategoryId: string | null = null;
  quotaNoticeShown = false;

  constructor(
    private readonly toastr: ToastrService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  get youtubeData(): YouTubeDestinationPlatformData | null {
    const data = this.platformData;
    return data && 'category_id' in data ? data : null;
  }

  get showQuotaNotice(): boolean {
    return this.catalog?.isDefaultCatalog === true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['platformData']) {
      this.syncFromPlatformData();
    }
    if (changes['catalog'] && this.showQuotaNotice && !this.quotaNoticeShown) {
      this.quotaNoticeShown = true;
      this.toastr.warning(
        'YouTube API quota was exceeded. Showing the default category list; regional categories may differ.',
        'YouTube categories',
      );
    }
  }

  onPrivacyChange(value: string): void {
    if (this.readonly) {
      return;
    }
    this.selectedPrivacy = value;
    this.emitPlatformData();
  }

  onCategoryChange(value: string): void {
    if (this.readonly) {
      return;
    }
    this.selectedCategoryId = value;
    this.emitPlatformData();
  }

  onMadeForKidsChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.madeForKids = checked;
    this.emitPlatformData();
  }

  onNotifySubscribersChange(checked: boolean): void {
    if (this.readonly) {
      return;
    }
    this.notifySubscribers = checked;
    this.emitPlatformData();
  }

  private syncFromPlatformData(): void {
    const pd = this.youtubeData;
    this.selectedPrivacy = pd?.privacy ?? 'Private';
    this.madeForKids = pd?.self_declared_made_for_kids ?? false;
    this.notifySubscribers = pd?.notify_subscribers ?? false;
    this.selectedCategoryId = pd?.category_id ?? null;
  }

  private emitPlatformData(): void {
    this.platformDataChange.emit({
      privacy: this.selectedPrivacy,
      self_declared_made_for_kids: this.madeForKids,
      notify_subscribers: this.notifySubscribers,
      category_id: this.selectedCategoryId,
    });
    this.changeDetectorRef.markForCheck();
  }
}
